import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PortableText, PortableTextComponents } from '@portabletext/react';
import imageUrlBuilder from '@sanity/image-url';
import { sanityClient } from '../helpers/sanityClient';
import Footer from '../components/Footer';
import TopNavigation from '../components/TopNavigation';
import { Helmet } from 'react-helmet-async';

interface Post {
    title: string;
    body: any;
    author?: string;
    mainImage?: { asset: { _ref: string; _type: string }; alt?: string };
    publishedAt?: string;
    metaDescription?: string;
    tags?: string[];
}

// Image builder
const builder = imageUrlBuilder(sanityClient);
function urlFor(source: any) {
    return builder.image(source);
}

// PortableText components
const components: PortableTextComponents = {
    types: {
        image: ({ value }) => (
            <img
                src={urlFor(value).width(800).url()}
                alt={value.alt || 'Blog post image'}
                className="my-6 rounded-lg mx-auto"
            />
        ),
    },
    marks: {
        strong: ({ children }) => <strong className="font-bold">{children}</strong>,
        em: ({ children }) => <em>{children}</em>,
        code: ({ children }) => (
            <code className="bg-gray-100 px-1 rounded font-mono">{children}</code>
        ),
    },
    block: {
        normal: ({ children }) => (
            <p className="mb-6 text-lg leading-relaxed font-speak-pro">{children}</p>
        ),
        h2: ({ children }) => (
            <h2 className="text-2xl font-bold mt-8 mb-4 font-typerighter">{children}</h2>
        ),
        h3: ({ children }) => (
            <h3 className="text-xl font-semibold mt-6 mb-3">{children}</h3>
        ),
    },
};

export default function BlogPost() {
    const navigate = useNavigate();
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<Post | null>(null);

    useEffect(() => {
        if (!slug) return;

        (sanityClient as any)
            .fetch(
                `*[_type == "post" && slug.current == $slug][0]{
          title,
          mainImage,
          body,
          author,
          publishedAt,
          metaDescription,
          tags
        }`,
                { slug }
            )
            .then(setPost)
            .catch(console.error);
    }, [slug]);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/blog');
        }
    };

    if (!post) return <div className="p-6">Loading…</div>;

    return (
        <>
            <Helmet>
                <title>{post.title} | Spelt Grazing</title>
                {post.metaDescription && (
                    <meta name="description" content={post.metaDescription} />
                )}
                {post.tags?.length ? (
                    <meta name="keywords" content={post.tags.join(', ')} />
                ) : null}
                {post.mainImage && (
                    <meta property="og:image" content={urlFor(post.mainImage).width(1200).url()} />
                )}
            </Helmet>

            <div className="flex flex-col min-h-screen">
                <TopNavigation />
                <main className="flex-grow p-6 max-w-3xl mx-auto bg-white">
                    <article>
                        <button
                            onClick={handleBack}
                            className="text-sm text-gray-600 hover:underline mb-4 flex items-center gap-1"
                        >
                            ← Back
                        </button>

                        <h1 className="text-4xl font-bold mb-6 font-speak-pro">{post.title}</h1>
                        {post.author && (
                            <p className="text-gray-600 mb-6 font-speak-pro italic">
                                Written by: <strong>{post.author}</strong> on{' '}
                                {post.publishedAt
                                    ? new Date(post.publishedAt).toLocaleDateString('en-GB')
                                    : 'Unknown date'}
                            </p>
                        )}
                        {post.mainImage && (
                            <img
                                src={urlFor(post.mainImage).width(800).url()}
                                alt={post.mainImage.alt || 'Blog post image'}
                                className="my-6 rounded-lg mx-auto"
                            />
                        )}
                        <div className="prose prose-lg prose-a:text-black prose-a:no-underline hover:prose-a:underline font-speak-pro text-xl">
                            <PortableText value={post.body} components={components} />
                        </div>
                    </article>
                </main>
                <Footer />
            </div>
        </>
    );
}
